import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import type { ApolloError } from "@apollo/client";

export interface GraphQLErrorInfo {
  message: string;
  isNetworkError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
  isServerError: boolean;
  retryable: boolean;
}

export function useGraphQLError() {
  const parseError = useCallback(
    (error: ApolloError | Error): GraphQLErrorInfo => {
      const message = error.message;

      const isNetworkError =
        message.includes("Network request failed") ||
        message.includes("Failed to fetch") ||
        message.includes("Network Error");

      const isAuthError =
        message.includes("Unauthorized") ||
        message.includes("Authentication required") ||
        message.includes("Token has expired") ||
        message.includes("token expired");

      const isValidationError =
        message.includes("Validation") ||
        message.includes("Invalid input") ||
        message.includes("Bad Request");

      const isServerError =
        message.includes("Internal Server Error") ||
        message.includes("500") ||
        message.includes("Server Error");

      const retryable = isNetworkError || isServerError;

      return {
        message,
        isNetworkError,
        isAuthError,
        isValidationError,
        isServerError,
        retryable,
      };
    },
    [],
  );

  const showErrorToast = useCallback(
    (error: ApolloError | Error, retryFn?: () => void) => {
      const errorInfo = parseError(error);

      if (errorInfo.isAuthError) {
        toast.error("Authentication required. Please log in again.", {
          action: {
            label: "Log In",
            onClick: () => {
              window.location.href = "/auth";
            },
          },
        });
        return;
      }

      if (errorInfo.isNetworkError) {
        toast.error(
          "Connection failed. Please check your internet connection and try again.",
          {
            action: retryFn
              ? {
                  label: "Retry",
                  onClick: retryFn,
                }
              : undefined,
          },
        );
        return;
      }

      if (errorInfo.isValidationError) {
        toast.error("Invalid input. Please check your data and try again.");
        return;
      }

      if (errorInfo.isServerError) {
        toast.error("Server error. Please try again later.", {
          action: retryFn
            ? {
                label: "Retry",
                onClick: retryFn,
              }
            : undefined,
        });
        return;
      }

      // Generic error
      toast.error("An error occurred. Please try again.", {
        action: retryFn
          ? {
              label: "Retry",
              onClick: retryFn,
            }
          : undefined,
      });
    },
    [parseError],
  );

  const handleError = useCallback(
    (
      error: ApolloError | Error,
      retryFn?: () => void,
      showToast: boolean = true,
    ) => {
      const errorInfo = parseError(error);

      if (showToast) {
        showErrorToast(error, retryFn);
      }

      return errorInfo;
    },
    [parseError, showErrorToast],
  );

  return useMemo(
    () => ({
      parseError,
      showErrorToast,
      handleError,
    }),
    [parseError, showErrorToast, handleError],
  );
}
